import {useContext} from 'react';
import {IDEContext} from '../contexts/IDEContext';
import {LeftColTitle} from '../LeftCol';
import styled from 'styled-components';

// a lot of these are just modules
const blacklist = ['___arg', 'run_karel_program', 'graphics', 'random'];

export const StepperView = () => {
  const {stepData, stepMode, hasHistory, stepPtr, stepList, pyodideClientRef} =
    useContext(IDEContext);

  const scopeName = stepList?.[stepPtr]?.codenm;
  const stepListPyproxy =
    pyodideClientRef.current?.getStepInfo()?.stepListPyproxy;
  const localsPyProxy = stepListPyproxy?.get?.(stepPtr)?.get?.('locals');
  const tableData = createTableData(stepData, localsPyProxy);
  return (
    <>
      <LeftColTitle title="Replay Mode" />

      {!hasHistory ? (
        <p className="m-3">
          You have to <b>run</b> your program first.
        </p>
      ) : (
        <>
          {!stepMode || stepPtr === stepList?.length - 1 ? (
            <p className="m-3">
              Scrub through the <b>Replay Timeline</b> next to the 'Run' button!
            </p>
          ) : null}
        </>
      )}
      {stepPtr !== stepList?.length - 1 ? (
        <div className="m-2 p-2 d-flex flex-column gap-2 overflow-auto">
          <div className="d-flex flex-row gap-2 flex-fill w-100">
            <table className="table caption-top" style={stepperTableStyle}>
              <colgroup>
                <col style={{width: '30%'}} /> {/* Name */}
                <col style={{width: '20%'}} /> {/* Type */}
                <col style={{width: '50%'}} /> {/* Value */}
              </colgroup>
              <caption>
                Variables for Scope:{' '}
                {scopeName === 'mainApp' ? 'global' : scopeName}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Type</th>
                  <th scope="col">Value</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, index) => {
                  return (
                    <tr key={index}>
                      <th scope="row">{row.variable}</th>
                      <td>{row.type}</td>
                      <td>{row.value}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
};

const isRenderable = v => {
  if (typeof v === 'string') return true;
  if (typeof v === 'number') return true;
  if (typeof v === 'boolean') return true;
  if (typeof v === 'object') {
    const toString = Object.prototype.toString.call(v);
    if (toString === '[object Array]') return true;
  }
  if (typeof v === 'function') return false;
  return true;
};

function isIterable(obj) {
  return typeof obj === 'object' && obj !== null && Symbol.iterator in obj;
}

export function createTableData(stepData, localsPyProxy) {
  if (!stepData) return [];

  if (!isIterable(stepData)) return [];

  // Save the original toString method
  let originalToString = Array.prototype.toString;

  // Override the toString method
  Array.prototype.toString = function () {
    return '[' + this.map(noneOrString).join(', ') + ']';
  };

  const noneOrString = value => (value == null ? 'None' : value.toString());

  const rows = [];
  for (let keyValue of stepData) {
    const key = keyValue[0];
    const value = keyValue[1];
    const canShow = isRenderable(value);

    // some objects are not renderable
    if (!canShow || blacklist.includes(key)) continue;

    if (Number.isNaN(value)) continue;
    const pyType = getPyType(value, key, localsPyProxy);
    let str_v = noneOrString(value);
    if (pyType === 'Canvas') {
      // str_v = str_v.replace("graphics.graphics.Canvas object", "Canvas")
      str_v = '<Canvas>';
    } else if (pyType === 'dict') {
      let entries = Array.from(value.entries());
      let readableString = entries
        .map(entry => `${entry[0]}: ${noneOrString(entry[1])}`)
        .join(', ');
      str_v = `{${readableString}}`;
    } else if (pyType === 'set') {
      let entries = Array.from(value);
      let readableString = entries
        .map(entry => `${noneOrString(entry)}`)
        .join(', ');
      str_v = `{${readableString}}`;
    } else if (str_v.includes('object at')) {
      // example:  <__main__.mainApp.<locals>.MyClass object at 0x148b0f8>
      str_v = str_v.match(/(?!.*\.)(.+)>/)[1];
    } else if (pyType === 'str') {
      // if the string is too long, truncate it
      if (str_v.length > 50) {
        str_v = str_v.slice(0, 50) + '...';
      }
      str_v = <PythonString>{quoteForPython(str_v)}</PythonString>;
    } else if (pyType === 'float' && Number.isInteger(value)) {
      str_v = str_v + '.0';
    }

    if (typeof value === 'boolean') {
      str_v = str_v.replace('true', 'True');
      str_v = str_v.replace('false', 'False');
    }
    rows.push({
      variable: key,
      type: pyType,
      value: str_v,
    });
  }

  // Restore the original toString method
  Array.prototype.toString = originalToString;

  return rows;
}

// TODO: get Pyodide types https://github.com/CodeinPlace/cip-webapp/issues/1128
//       for example, Tuples are not supported currently
const getPyType = (value, key, localsPyProxy) => {
  if (value === null || value === undefined) {
    return 'None';
  } else if (value instanceof Map) {
    return 'dict';
  } else if (value instanceof Array) {
    return 'list';
  } else if (value instanceof Set) {
    return 'set';
  } else if (value instanceof Object) {
    // PyProxy objects from pyodide (for modules and classes) have type property
    if (value && value.type) {
      return value.type;
    } else {
      return 'dict';
    }
  }

  // handle primitive types
  const jsType = typeof value;
  switch (jsType) {
    case 'boolean':
      return 'bool';
    case 'string':
    case 'symbol':
      return 'str';
    case 'number':
      if (Number.isInteger(value)) {
        // if the value is an integer, we need to check if it's string
        // representation ends in .0 to determine if it's an int or a float
        const endsWithDotZeroRe = new RegExp(`'${key}': \[+-\]?\\d+\\.0`);
        const isFloat = endsWithDotZeroRe.test(localsPyProxy?.toString());
        return isFloat ? 'float' : 'int';
      } else {
        return 'float';
      }
    case 'bigint':
      return 'int';
    default:
      return ''; // if type cannot be inferred, return empty string
  }
};

/**
 * Wrap a JavaScript string in the shortest legal Python literal.
 *
 * Strategy:
 *   1. 'single quotes'  – if the text contains no single quotes.
 *   2. "double quotes"  – if the text contains no double quotes.
 *   3. '''triple-single''' – if it doesn’t contain that sequence.
 *   4. """triple-double""" – likewise.
 *   5. Fall back to single quotes with \' escapes.
 */
export function quoteForPython(raw) {
  if (!raw.includes("'")) {
    return `'${raw}'`;
  }
  if (!raw.includes('"')) {
    return `"${raw}"`;
  }
  if (!raw.includes("'''")) {
    return `'''${raw}'''`;
  }
  if (!raw.includes('"""')) {
    return `"""${raw}"""`;
  }
  return `'${raw.replace(/'/g, "\\'")}'`;
}

const stepperTableStyle = {
  fontSize: '12px',
  fontFamily:
    'Monaco, Menlo, "Ubuntu Mono", Consolas, source-code-pro, monospace',
};

const PythonString = styled.span`
  color: #95261f;
  font-weight: bold;
`;
